import { useState, useEffect, useCallback } from "react";
import { PERIOD_PRESETS, getDateRange } from "./periodUtils";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { TrendingUp, DollarSign, FileText, ArrowUpRight, Lock, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicAxios = axios.create({ withCredentials: false });

const fmtC = (v) => {
  const abs = Math.abs(v || 0);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1000000) return `${sign}€${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}€${(abs / 1000).toFixed(1)}K`;
  return `${sign}€${abs.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const fmtCFull = (v) => {
  const abs = Math.abs(v || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}€${abs.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const SECTOR_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16"];
const SALE_TYPE_COLORS = ["#0ea5e9", "#f59e0b"];

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border border-border/60 rounded-lg px-3 py-2 shadow-lg">
      {label && <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>}
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          <span className="font-medium">{entry.name}: </span>
          {formatter ? formatter(entry.value) : fmtCFull(entry.value)}
        </p>
      ))}
    </div>
  );
};

// Password gate
const PasswordGate = ({ onVerify, loading }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password) { setError("Inserisci la password"); return; }
    setError("");
    onVerify(password);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="investor-password-gate">
      <Card className="w-full max-w-md border-border/60">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
            <Lock className="h-5 w-5 text-muted-foreground" />
          </div>
          <CardTitle className="text-xl">Dashboard Protetta</CardTitle>
          <p className="text-sm text-muted-foreground">Questa dashboard richiede una password per l&apos;accesso.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              data-testid="investor-password-input"
            />
            {error && <p className="text-sm text-red-500" data-testid="investor-password-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="investor-password-submit">
              {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" /> : "Accedi"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Main investor dashboard content
const InvestorContent = ({ data }) => {
  const { kpis, by_sector, by_sale_type } = data;
  const monthly_trend = (data.monthly_trend || []).map(m => ({ ...m, month: m.partial ? `${m.month} (in corso)` : m.month }));

  return (
    <div className="space-y-8" data-testid="investor-dashboard-content">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">ARR</p>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </div>
            <p className="text-3xl font-bold text-emerald-700" data-testid="inv-kpi-arr">{fmtC(kpis.total_arr)}</p>
            <p className="text-xs text-emerald-600 mt-1" data-testid="inv-kpi-snapshot">Annual Recurring Revenue attivo al {kpis.snapshot_date ? new Date(kpis.snapshot_date).toLocaleDateString("it-IT") : "—"}</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">MRR</p>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold" data-testid="inv-kpi-mrr">{fmtC(kpis.total_mrr)}</p>
            <p className="text-xs text-muted-foreground mt-1">Monthly Recurring Revenue</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contratti Attivi</p>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold" data-testid="inv-kpi-active">{kpis.active_count}</p>
            <p className="text-xs text-muted-foreground mt-1">contratti attivi</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Valore Medio Contratti Attivi</p>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold" data-testid="inv-kpi-avg-deal">{fmtC(kpis.avg_deal)}</p>
            <p className="text-xs text-muted-foreground mt-1">Valore medio contratto attivo</p>
          </CardContent>
        </Card>
      </div>

      {/* KPI Row 2: Licenses & Clients (active only) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-border/60">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Licenze Attive</p>
            <p className="text-3xl font-bold mt-2" data-testid="inv-kpi-licenses">{kpis.active_licenses}</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Clienti Attivi</p>
            <p className="text-3xl font-bold mt-2" data-testid="inv-kpi-clients">{kpis.active_clients}</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Licenze / Contratto</p>
            <p className="text-3xl font-bold mt-2" data-testid="inv-kpi-lic-contract">{kpis.avg_licenses_per_contract}</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Licenze / Cliente</p>
            <p className="text-3xl font-bold mt-2" data-testid="inv-kpi-lic-client">{kpis.avg_licenses_per_client}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1: ARR Trend (full width) */}
      <Card className="border-border/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Crescita ARR</CardTitle>
        </CardHeader>
          <CardContent>
            {monthly_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthly_trend}>
                  <defs>
                    <linearGradient id="invArrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} padding={{ left: 10, right: 30 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="linear" dataKey="cumulative_arr" name="ARR Cumulativo" stroke="#10b981" fill="url(#invArrGrad)" strokeWidth={2.5} dot={{ r: 3, fill: "#10b981" }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato disponibile</div>
            )}
          </CardContent>
      </Card>

      {/* Charts Row 2: Sector + Sale Type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">ARR per Settore</CardTitle>
          </CardHeader>
          <CardContent>
            {by_sector.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(200, by_sector.length * 50)}>
                <BarChart data={by_sector} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="sector" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={130} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="arr" name="ARR" radius={[0, 4, 4, 0]}>
                    {by_sector.map((_, i) => <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">New vs Up-Sell</CardTitle>
          </CardHeader>
          <CardContent>
            {by_sale_type.some(s => s.arr > 0) ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={by_sale_type.filter(s => s.arr > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="arr" nameKey="name">
                    {by_sale_type.filter(s => s.arr > 0).map((entry, i) => (
                      <Cell key={entry.name} fill={SALE_TYPE_COLORS[i % SALE_TYPE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Charts: New Contracts + New ARR */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Nuovi Contratti per Mese</CardTitle>
          </CardHeader>
          <CardContent>
            {monthly_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} padding={{ left: 10, right: 30 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip content={<ChartTooltip formatter={(v) => `${v}`} />} />
                  <Bar dataKey="new_contracts" name="Nuovi Contratti" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Nuovo ARR Mensile</CardTitle>
          </CardHeader>
          <CardContent>
            {monthly_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} padding={{ left: 10, right: 30 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="arr_added" name="Nuovo ARR" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main component
export default function SharedInvestorView() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading, password, ready, error
  const [data, setData] = useState(null);
  const [linkName, setLinkName] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [saleTypeFilter, setSaleTypeFilter] = useState(["New", "Up-Sell"]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [periodPreset, setPeriodPreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const fetchData = useCallback(async (filterTypes, period, cFrom, cTo) => {
    try {
      const params = new URLSearchParams();
      if (filterTypes.length < 2) params.set("sale_types", filterTypes.join(","));
      const range = period === "custom" ? { from: cFrom, to: cTo } : getDateRange(period);
      if (range.from) params.set("date_from", range.from);
      if (range.to) params.set("date_to", range.to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await publicAxios.get(`${API}/investor/${token}${qs}`);
      setLinkName(res.data.link_name || "");
      if (res.data.password_required) {
        setState("password");
      } else {
        setData(res.data.data);
        setState("ready");
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Dashboard non trovata");
      setState("error");
    }
    setInitialLoad(false);
  }, [token]);

  useEffect(() => { fetchData(saleTypeFilter, periodPreset, customFrom, customTo); }, [fetchData, saleTypeFilter, periodPreset, customFrom, customTo]);

  const handleVerify = async (password) => {
    setVerifying(true);
    try {
      const params = new URLSearchParams();
      if (saleTypeFilter.length < 2) params.set("sale_types", saleTypeFilter.join(","));
      const range = periodPreset === "custom" ? { from: customFrom, to: customTo } : getDateRange(periodPreset);
      if (range.from) params.set("date_from", range.from);
      if (range.to) params.set("date_to", range.to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await publicAxios.post(`${API}/investor/${token}/verify${qs}`, { password });
      setData(res.data.data);
      setLinkName(res.data.link_name || "");
      setState("ready");
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Password non corretta");
      setState("password");
    }
    setVerifying(false);
  };

  const toggleSaleType = (type) => {
    setSaleTypeFilter(prev => {
      if (prev.includes(type)) {
        return prev.length > 1 ? prev.filter(t => t !== type) : prev;
      }
      return [...prev, type];
    });
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="investor-error">
        <Card className="w-full max-w-md border-border/60 text-center">
          <CardContent className="p-8">
            <FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-lg font-medium mb-1">Dashboard non disponibile</p>
            <p className="text-sm text-muted-foreground">{errorMsg}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "password") {
    return <PasswordGate onVerify={handleVerify} loading={verifying} />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="shared-investor-view">
      {/* Header */}
      <header className="border-b border-border/60 bg-background">
        <div className="max-w-[1400px] mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/api/static/logo" alt="Aiclo" className="h-9" />
              <div>
                <h1 className="text-xl font-bold tracking-tight">Investor Dashboard</h1>
                <p className="text-xs text-muted-foreground mt-0.5">{linkName || "Sales & Revenue Metrics"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-0.5" data-testid="inv-sale-type-filter">
                {["New", "Up-Sell"].map(type => (
                  <button
                    key={type}
                    onClick={() => toggleSaleType(type)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      saleTypeFilter.includes(type)
                        ? "bg-background shadow-sm text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`inv-filter-${type.toLowerCase()}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
              <select className="h-7 rounded-md border border-input bg-background px-2 text-xs" value={periodPreset} onChange={(e) => setPeriodPreset(e.target.value)} data-testid="inv-filter-period">
                {PERIOD_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              {periodPreset === "custom" && (
                <div className="flex items-center gap-1">
                  <input type="date" className="h-7 rounded-md border border-input bg-background px-1 text-xs w-[110px]" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="inv-custom-from" />
                  <span>—</span>
                  <input type="date" className="h-7 rounded-md border border-input bg-background px-1 text-xs w-[110px]" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="inv-custom-to" />
                </div>
              )}
              <Eye className="h-3.5 w-3.5 ml-1" />
              <span>Vista di sola lettura</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <InvestorContent data={data} />
      </main>
    </div>
  );
}
